import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import settings from '/@/core/settings';
import { useAllFormatedMaps } from '/@/hooks/maps';
import { _ } from '/@/locale';
import { getAppInstance, getEnableSpodoptera, getSessionCountry } from '/@/store/session/selectors';
import NOLHeader from './header';
import NOLItemList from './item-list';
import NOLMapValues from './map-values';
import NOLRowList from './row-list';
import styles from './styles.module.css';

// Support 2 levels
const NewOverlayList = ({ goBack, field }) => {
    const appInstance = useSelector(getAppInstance);
    const sessionCountry = useSelector(getSessionCountry);
    const enableSpodoptera = useMemo(() => getEnableSpodoptera(appInstance), [appInstance]);
    const [allMapValues, setAllMapValues] = useState([]);
    const { data: maps, isValidating: loading } = useAllFormatedMaps({ country: sessionCountry.code });
    useEffect(() => {
        if (maps && !loading) {
            const exclude = ['spodoptera_forecast'];
            const overlays = maps.filter(({ type }) => !exclude.includes(type));
            setAllMapValues(overlays);
        }
    }, [maps]);

    const overlayConfig = {
        label: _('Enfermedades, Plagas y Malezas'),
        icon: 'diseases',
        color: settings.app.theme === 'expertobayer' ? 'expertobayer' : 'blue',
        children: [
            {
                icon: 'corn',
                label: _('Maíz'),
                color: settings.app.theme === 'expertobayer' ? 'expertobayer' : 'orange',
                type: 'corn',
                children: [
                    { label: _('Enfermedades'), type: 'corn_diseases_historic_pressure' },
                    { label: _('Plagas'), type: 'corn_pests_historic_pressure' },
                    { label: _('Malezas'), type: 'weeds_rem' },
                    { label: _('Caracteres Agrónomicos'), type: 'corn_others' },
                ],
                id: 'corn',
            },
            {
                icon: 'circleSpodoptera',
                label: _('Pronóstico de Spodoptera'),
                type: 'spodoptera_forecast',
                color: settings.app.theme === 'expertobayer' ? 'expertobayer' : 'orange',
                id: 'spodoptera_forecast',
            },

            {
                icon: 'humidityCircle',
                label: _('Humedad en el suelo'),
                type: 'humidity',
                color: settings.app.theme === 'expertobayer' ? 'expertobayer' : 'blue',
                id: 'humidity',
            },
        ],
    };

    const [filteredOverlayConfig, setFilteredOverlayConfig] = useState(overlayConfig);
    const [selectedItem, setSelectedItem] = useState(undefined);
    const [selectedRowItem, setSelectedRowItem] = useState(undefined);
    const [selectedMapValues, setSelectedMapValues] = useState([]);

    const selectItem = (item) => {
        setSelectedItem(item);
        setSelectedRowItem(undefined);
        if (item.children === undefined) {
            setMapValues(item);
        } else {
            setSelectedMapValues([]);
            selectRowItem(item.children[0]);
        }
    };

    const selectRowItem = (item) => {
        setSelectedRowItem(item);
        setMapValues(item);
    };

    const setMapValues = (item) => {
        const fieldMapValues = field.mapValues.map((mv) => {
            let foundVersion;
            const allValue = allMapValues.find((element) => {
                foundVersion = element.versions.find((version) => version.id === mv.map);
                return !!foundVersion;
            });
            return {
                ...mv,
                map: {
                    ...allValue,
                    id: mv.map,
                    ...(foundVersion && {
                        versionLabel: foundVersion.versionLabel,
                        referenceUnit: foundVersion.referenceUnit,
                    }),
                },
            };
        });
        const mapValues = fieldMapValues
            .filter((mapValue) => {
                return mapValue.map && mapValue.map.type == item.type;
            })
            .reduce((versionedMapValues, mapValue) => {
                // Agrupo Map Values x Map (para versionar)
                if (!versionedMapValues.hasOwnProperty(mapValue.map.slug)) {
                    versionedMapValues[mapValue.map.slug] = [];
                }

                versionedMapValues[mapValue.map.slug].push(mapValue);

                return versionedMapValues;
            }, {});
        setSelectedMapValues(mapValues);
    };

    const filterConfigFunction = (item) => {
        const fieldMapValues = field.mapValues.map((mv) => {
            const allValue = allMapValues.find((element) => {
                return element.id === mv.map && element.type === mv.type;
            });
            return { ...mv, map: allValue };
        });
        if (item.children) return true;
        return fieldMapValues.find((mv) => mv.map && mv.map.type === item.type);
    };

    const filterConfig = () => {
        const children = overlayConfig.children
            .filter(filterConfigFunction)
            .map((item) => {
                if (!item.children) return item;
                return {
                    ...item,
                    children: item.children.filter(filterConfigFunction),
                };
            })
            .filter((item) => {
                if (item.type === 'spodoptera_forecast') return enableSpodoptera;
                if (!item.children) return true;
                return item.children.length > 0;
            });

        const newOverlayConfig = {
            ...overlayConfig,
            children,
        };

        setFilteredOverlayConfig(newOverlayConfig);
    };

    useEffect(() => {
        filterConfig();
    }, [allMapValues]);

    if (!allMapValues.length) return null;

    return (
        <div className={styles.root}>
            {goBack && <NOLHeader overlayConfig={filteredOverlayConfig} goBack={goBack} />}
            {loading ? (
                <Grid container flexDirection='column' spacing={1} sx={{ mt: 0.5 }}>
                    {Array.from(new Array(2)).map((_, index) => (
                        <Grid item key={index}>
                            <Skeleton variant='rectangular' width='100%' height={50} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Fragment>
                    <NOLItemList
                        selectItem={selectItem}
                        itemList={filteredOverlayConfig['children']}
                        selectedItem={selectedItem}
                        weeds
                    />
                    {selectedItem && selectedItem['children'] !== undefined && (
                        <NOLRowList
                            selectedItem={selectedItem}
                            selectedRowItem={selectedRowItem}
                            selectRowItem={selectRowItem}
                        />
                    )}
                    <NOLMapValues mapValues={selectedMapValues} />
                </Fragment>
            )}
        </div>
    );
};

export default NewOverlayList;
